/**
 * useEligibleModelsAcrossProviders — aggregates enabled, capability-matching
 * models for an org-default slot and retains backend selection-policy metadata.
 * Policy-ineligible rows stay in the result so selectors can show the saved
 * model label plus an actionable disabled reason instead of hiding context.
 *
 * Data flow:
 *   1. Read providers list from cache (useLLMProviders).
 *   2. For each provider, issue a parallel useQuery for its model list.
 *      If the cache is warm (from MainPane's models section), no extra
 *      network calls are triggered. First edit may show a brief loading
 *      state while fetches resolve.
 *   3. Flatten + filter: model.enabled === true AND
 *      model.capabilities[capabilityKey] === true.
 *   4. Attach providerName to each model for combobox label rendering.
 *
 * Binding contract: callers MUST bind combobox selection by model.id (UUID),
 * never by array index — this hook's output can reorder when a sync mutation
 * invalidates+refetches a provider's model list mid-selection.
 */

import { useQueries } from "@tanstack/react-query";
import { useLLMProviders } from "@/hooks/api/queries/llm-providers.queries";
import { llmModelService } from "@/lib/api/services/llm-model.service";
import { llmModelKeys } from "@/hooks/api/keys/llm-models.keys";
import { USE_CASE_CAPABILITY_MAP } from "../constants/use-case-capability-map";
import { USE_CASE_PROVIDER_TYPE_ALLOWLIST } from "../constants/use-case-provider-types";
import type { LLMModel, UseCase } from "@/types/llm-model";
import type { LLMProvider } from "@/types/llm-provider";
import type { PaginatedResponse } from "@/types/api.types";

/** A model enriched with its provider's display name for combobox label rendering. */
export interface EligibleModel {
  /** UUID — use this as the combobox option value; never bind by array index. */
  id: string;
  /** modelId string (e.g. "gemini-1.5-pro") shown in the label. */
  modelId: string;
  /** Provider displayName or name for the "Provider · modelId" label. */
  providerName: string;
  /** Provider id — needed in the mutation payload. */
  providerId: string;
  /** Full model record for capability checks downstream. */
  model: LLMModel;
  /** Backend-owned selection policy for the requested use case. */
  useCaseEligible?: boolean;
  ineligibleReason?: string;
}

interface UseEligibleModelsResult {
  models: EligibleModel[];
  /** True while any provider's model list is still loading. */
  isLoading: boolean;
}

export function useEligibleModelsAcrossProviders(
  useCase: UseCase,
): UseEligibleModelsResult {
  const providersQuery = useLLMProviders();
  const providers: LLMProvider[] =
    (providersQuery.data as { data?: LLMProvider[] } | undefined)?.data ?? [];

  // Issue a parallel query for each provider's model list.
  // useQueries keeps individual query states separate so one slow provider
  // does not block the others from appearing in the combobox.
  const modelQueries = useQueries({
    queries: providers.map((provider) => ({
      queryKey: llmModelKeys.byProvider(provider.id),
      queryFn: () => llmModelService.listByProvider(provider.id),
      staleTime: 60 * 1000,
      enabled: !!provider.id,
    })),
  });

  const capabilityKey = USE_CASE_CAPABILITY_MAP[useCase];
  // Provider-type gate: empty list = no constraint (e.g. chat/embedding).
  // Non-empty = BE only supports this set today, so even capability-
  // matching models on other vendors must be filtered out — otherwise
  // the admin binds a model that the BE resolver will reject at runtime.
  const allowedProviderTypes = USE_CASE_PROVIDER_TYPE_ALLOWLIST[useCase];
  const enforceProviderType = allowedProviderTypes.length > 0;

  const models: EligibleModel[] = [];
  for (let i = 0; i < providers.length; i++) {
    const provider = providers[i];
    if (
      enforceProviderType &&
      !allowedProviderTypes.includes(provider.providerType)
    ) {
      continue;
    }
    const query = modelQueries[i];
    const rawModels =
      (query.data as PaginatedResponse<LLMModel> | undefined)?.data ?? [];

    for (const model of rawModels) {
      // A model is eligible if: enabled AND has the required capability set.
      if (!model.enabled) continue;
      if (!model.capabilities[capabilityKey]) continue;

      models.push({
        id: model.id,
        modelId: model.modelId,
        providerName: provider.displayName || provider.name,
        providerId: provider.id,
        model,
        useCaseEligible: model.useCaseEligibility?.[useCase]?.eligible !== false,
        ineligibleReason: model.useCaseEligibility?.[useCase]?.reason,
      });
    }
  }

  const isLoading =
    providersQuery.isLoading ||
    modelQueries.some((q) => q.isLoading);

  return { models, isLoading };
}
